import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Send,
  History,
  LogOut,
  Diamond,
  Package,
  Receipt,
  Users,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { InstallAppButton } from "@/components/InstallAppButton";

const NAV = [
  { to: "/reseller", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/reseller/recharge", icon: Send, label: "New Recharge" },
  { to: "/reseller/stocks", icon: Package, label: "Stocks" },
  { to: "/reseller/allocations", icon: Diamond, label: "Allocations" },
  { to: "/reseller/payments", icon: Receipt, label: "Payments" },
  { to: "/reseller/history", icon: History, label: "History" },
];

export default function ResellerLayout() {
  const { reseller, logout } = useAuth();
  const isSub = !!reseller?.is_subagent;
  const { pathname } = useLocation();
  const nav = NAV.filter((n) => !(isSub && ["/reseller/stocks", "/reseller/payments"].includes(n.to)))
    .concat(isSub ? [] : [{ to: "/reseller/subagents", icon: Users, label: "Sub-agents" }]);
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/reseller/login", { replace: true });
  };

  if (isSub && ["/reseller/stocks", "/reseller/payments", "/reseller/subagents"].includes(pathname)) return <Navigate to="/reseller" replace />;
  return (
    <div className="min-h-screen bg-zinc-50 flex">
      <aside className="hidden md:flex fixed inset-y-0 left-0 z-40 w-64 bg-zinc-950 text-zinc-300 flex-col">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-zinc-800">
          <Diamond className="w-5 h-5 text-white" strokeWidth={1.5} />
          <div>
            <div className="font-display text-sm font-semibold text-white leading-none">Shinning Eyes</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1" data-testid="reseller-role-label">{isSub ? "Sub-agent" : "Reseller"}</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`reseller-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-zinc-800 text-white font-medium"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-zinc-800">
          <div className="px-3 py-2 mb-2">
            <div className="text-xs text-zinc-500">Signed in as</div>
            <div className="text-sm text-white font-medium truncate" data-testid="reseller-user-email">
              {reseller?.email}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="reseller-logout-button"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-zinc-400 hover:bg-zinc-900 hover:text-white"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 md:ml-64">
        <header className="sticky top-0 z-30 h-16 flex items-center justify-between border-b border-zinc-200 bg-white/80 backdrop-blur-md px-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{isSub ? `Sub-agent${reseller?.parent_name ? ` · ${reseller.parent_name}` : ""}` : "Reseller"}</div>
            <div className="font-display text-sm font-semibold text-zinc-900">
              {reseller?.name || reseller?.email}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <InstallAppButton testid="reseller-install-app-button" variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-white border-0" />
            <button
              onClick={handleLogout}
              className="md:hidden text-sm text-zinc-600 hover:text-zinc-900"
              data-testid="reseller-logout-button-mobile"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>
        <nav className="md:hidden flex gap-1 overflow-x-auto border-b border-zinc-200 bg-white px-3 py-2" data-testid="reseller-mobile-nav">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`reseller-mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs ${isActive ? "bg-zinc-900 text-white" : "text-zinc-600"}`}>
              <n.icon className="w-3.5 h-3.5" />{n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 sm:p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
